import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, safeInternalPath } from "../api";
import { toast } from "sonner";
import { ORG_TYPE_CODES } from "../lib/orgTypes";

export default function RegisterOrg() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = safeInternalPath(params.get("next"));
  const [f, setF] = useState({
    email:"", password:"", org_name:"", phone:"",
    address_street:"", address_city:"", country:"IT",
    org_type:"", org_type_other:"",
    clinical_declared:null,
    tax_id:"", vat_id:"",
    contact_name:"", contact_role:"", contact_email:"", contact_phone:"",
    accept_terms:false
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.org_type) return toast.error(t("orgx.type_required"));
    if (f.clinical_declared === null) return toast.error(t("orgx.clinical_required"));
    if (!f.accept_terms) return toast.error(t("auth.terms_agree"));
    try {
      await api.post("/auth/register/organization", { ...f, vat_id: f.vat_id || null, org_type_other: f.org_type === "altro" ? (f.org_type_other || null) : null });
      toast.success(t("auth.org_success"));
      nav(`/confirm-email?email=${encodeURIComponent(f.email)}${nextPath ? `&next=${encodeURIComponent(nextPath)}` : ""}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  return (<Layout><div className="yp-container max-w-3xl py-10">
    <h1 className="text-3xl font-bold mb-2">{t("auth.register_org_title")}</h1>
    <p className="text-sm text-slate-500 mb-6">{t("auth.org_subtitle")}</p>
    <form onSubmit={submit} className="yp-card p-6 space-y-4" data-testid="register-org-form">
      <div className="grid md:grid-cols-2 gap-4">
        <input required className="yp-input md:col-span-2" placeholder={t("auth.org_name")} value={f.org_name} onChange={e=>set("org_name",e.target.value)} data-testid="reg-org-name" />
        <input required className="yp-input" type="email" placeholder={t("common.email")} value={f.email} onChange={e=>set("email",e.target.value)} data-testid="reg-org-email" />
        <input required className="yp-input" type="password" minLength="8" placeholder={t("common.password")} value={f.password} onChange={e=>set("password",e.target.value)} data-testid="reg-org-password" />
        <input required className="yp-input" placeholder={t("auth.org_phone")} value={f.phone} onChange={e=>set("phone",e.target.value)} data-testid="reg-org-phone" />
        <select className="yp-input" value={f.country} onChange={e=>set("country",e.target.value)} data-testid="reg-org-country">
          {["IT","DE","FR","AT","CH","US","UK"].map(c=><option key={c}>{c}</option>)}
        </select>
        <input required className="yp-input" placeholder={t("auth.address_street")} value={f.address_street} onChange={e=>set("address_street",e.target.value)} data-testid="reg-org-street" />
        <input required className="yp-input" placeholder={t("auth.address_city")} value={f.address_city} onChange={e=>set("address_city",e.target.value)} data-testid="reg-org-city" />
        <input required className="yp-input" placeholder={t("auth.org_tax_id")} value={f.tax_id} onChange={e=>set("tax_id",e.target.value)} data-testid="reg-org-tax" />
        <input className="yp-input" placeholder={t("auth.org_vat")} value={f.vat_id} onChange={e=>set("vat_id",e.target.value)} data-testid="reg-org-vat" />
        <select required className="yp-input md:col-span-2" value={f.org_type} onChange={e=>set("org_type",e.target.value)} data-testid="reg-org-type" aria-label={t("orgx.select_type")}>
          <option value="">{t("orgx.select_type")}</option>
          {ORG_TYPE_CODES.map(c => <option key={c} value={c}>{t(`orgx.type_${c}`)}</option>)}
        </select>
        {f.org_type === "altro" && (
          <input required className="yp-input md:col-span-2" placeholder={t("orgx.type_other_ph")} value={f.org_type_other} onChange={e=>set("org_type_other",e.target.value)} data-testid="reg-org-type-other" />)}
      </div>
      <div className="space-y-2" data-testid="reg-org-clinical">
        <div className="font-semibold text-sm">{t("orgx.clinical_type_label")} *</div>
        <label className={`flex items-start gap-2 text-sm p-3 rounded-lg border cursor-pointer ${f.clinical_declared === true ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
          <input type="radio" name="clinical" className="mt-1" checked={f.clinical_declared === true} onChange={()=>set("clinical_declared", true)} data-testid="reg-org-clinical-yes" />
          <span><strong>{t("orgx.clinical_with")}</strong><br /><span className="text-slate-500">{t("orgx.clinical_with_desc")}</span></span>
        </label>
        <label className={`flex items-start gap-2 text-sm p-3 rounded-lg border cursor-pointer ${f.clinical_declared === false ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
          <input type="radio" name="clinical" className="mt-1" checked={f.clinical_declared === false} onChange={()=>set("clinical_declared", false)} data-testid="reg-org-clinical-no" />
          <span><strong>{t("orgx.clinical_without")}</strong><br /><span className="text-slate-500">{t("orgx.clinical_without_desc")}</span></span>
        </label>
      </div>
      <hr className="border-slate-200" />
      <h3 className="font-semibold">{t("auth.contact_section")}</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <input required className="yp-input" placeholder={t("auth.contact_name")} value={f.contact_name} onChange={e=>set("contact_name",e.target.value)} data-testid="reg-org-contact-name" />
        <input required className="yp-input" placeholder={t("auth.contact_role")} value={f.contact_role} onChange={e=>set("contact_role",e.target.value)} data-testid="reg-org-contact-role" />
        <input required className="yp-input" type="email" placeholder={t("auth.contact_email")} value={f.contact_email} onChange={e=>set("contact_email",e.target.value)} data-testid="reg-org-contact-email" />
        <input required className="yp-input" placeholder={t("auth.contact_phone")} value={f.contact_phone} onChange={e=>set("contact_phone",e.target.value)} data-testid="reg-org-contact-phone" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.accept_terms} onChange={e=>set("accept_terms",e.target.checked)} data-testid="reg-org-terms" />
        {t("auth.terms_agree")}
      </label>
      <button className="yp-btn yp-btn-primary w-full" type="submit" data-testid="reg-org-submit">{t("nav.register")}</button>
    </form>
  </div></Layout>);
}
